import { type Dispatch, type SetStateAction } from 'react'
import { Edit3, FlaskConical, Image, Info, Radio, Square, Trash2 } from 'lucide-react'
import {
  getChannelCoverUrl,
  resolveMediaUrl,
  type Channel,
  type ChannelFilters,
  type StreamStatusResponse
} from '../../../api'
import { EmptyState, IconButton, MobileEntityList, MobileEntityRow, SearchField, ToggleSwitch } from '../../../components/ui'
import { ChannelStreamInlineControls } from '../ChannelStreamInlineControls'
import styles from './ChannelMobileList.module.css'

type ChannelMobileListProps = {
  channels: Channel[]
  filters: ChannelFilters
  onDelete: (channel: Channel) => Promise<void>
  onEdit: (channel: Channel) => void
  onToggleStreamPlayback: (channel: Channel) => void
  onOpenPlayground: (channel: Channel) => void
  onStartStream: (channel: Channel) => Promise<void>
  onStopStream: (channel: Channel) => Promise<void>
  onToggleActive: (channel: Channel, active: boolean) => Promise<void>
  openInfoChannelId: string | null
  playingStreamChannelId: string | null
  search: string
  setOpenInfoChannelId: Dispatch<SetStateAction<string | null>>
  startingStreamChannelId: string | null
  stoppingStreamChannelId: string | null
  streamPlaybackLoading: boolean
  streamPlaybackPlaying: boolean
  streamStatuses: Record<string, StreamStatusResponse | undefined>
  streamStatusesLoading: boolean
  onSearchChange: (value: string) => void
}

export const ChannelMobileList = ({
  channels,
  filters,
  onDelete,
  onEdit,
  onToggleStreamPlayback,
  onOpenPlayground,
  onStartStream,
  onStopStream,
  onToggleActive,
  openInfoChannelId,
  playingStreamChannelId,
  search,
  setOpenInfoChannelId,
  startingStreamChannelId,
  stoppingStreamChannelId,
  streamPlaybackLoading,
  streamPlaybackPlaying,
  streamStatuses,
  streamStatusesLoading,
  onSearchChange
}: ChannelMobileListProps) => (
  <MobileEntityList
    toolbar={(
      <SearchField
        className={styles.searchField ?? ''}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search channels..."
        size="mobile"
        value={search}
      />
    )}
    empty={channels.length === 0 ? (
      <EmptyState message={filters.search ? 'No channels match these filters.' : 'No channels yet.'} />
    ) : null}
  >
    {channels.map((channel) => {
      const vgCount = channel.visual_groups.length
      const agCount = channel.audio_groups.length
      const infoOpen = openInfoChannelId === channel.id
      const coverUrl = getChannelCoverUrl(channel, 'thumb')
      const streamStatus = streamStatuses[channel.id]
      const streamStatusKnown = Object.prototype.hasOwnProperty.call(streamStatuses, channel.id)
      const streamStatusLoading = streamStatusesLoading && !streamStatusKnown
      const streamStarting = startingStreamChannelId === channel.id
      const streamStopping = stoppingStreamChannelId === channel.id
      const isCurrentStreamPlayback = playingStreamChannelId === channel.id
      const streamListenLoading = isCurrentStreamPlayback && streamPlaybackLoading
      const streamListenPlaying = isCurrentStreamPlayback && streamPlaybackPlaying
      const subtitle = channel.description || streamStatus?.status === 'error' ? (
        <>
          {channel.description ?? ''}
          {streamStatus?.status === 'error' && <span className={styles.streamErrorPill}>Stream error</span>}
        </>
      ) : undefined

      return (
        <MobileEntityRow
          key={channel.id}
          leadingWidth={86}
          ariaLabel={`Edit ${channel.name}`}
          leading={(
            <ChannelStreamInlineControls
              channel={channel}
              loading={streamStatusLoading}
              playbackLoading={streamListenLoading}
              playbackPlaying={streamListenPlaying}
              starting={streamStarting}
              status={streamStatus}
              stopping={streamStopping}
              onStart={onStartStream}
              onStop={onStopStream}
              onTogglePlayback={onToggleStreamPlayback}
            />
          )}
          media={coverUrl ? (
            <img className={styles.cover} src={resolveMediaUrl(coverUrl)} alt="" loading="lazy" />
          ) : (
            <span className={styles.coverPlaceholder}>
              <Image size={16} />
            </span>
          )}
          onSelect={() => onEdit(channel)}
          subtitle={subtitle}
          title={channel.name}
          actions={(
            <div className={[styles.actionsCell, 'channel-mobile-actions-cell'].join(' ')}>
              <IconButton
                aria-expanded={infoOpen}
                icon={<Info size={15} />}
                label={`Channel info for ${channel.name}`}
                onClick={() => setOpenInfoChannelId((current) => current === channel.id ? null : channel.id)}
                title="Channel info"
              />
              {infoOpen && (
                <div className={['track-info-popover', 'channel-info-popover', styles.infoPopover].join(' ')} role="dialog" aria-label={`Details for ${channel.name}`}>
                  <div>
                    <span>Active</span>
                    <ToggleSwitch
                      checked={channel.is_active}
                      label={`Toggle ${channel.name} active state`}
                      onChange={(event) => {
                        setOpenInfoChannelId(null)
                        void onToggleActive(channel, event.target.checked)
                      }}
                    />
                  </div>
                  <div>
                    <span>Visual groups</span>
                    <strong>{vgCount}</strong>
                  </div>
                  <div>
                    <span>Audio groups</span>
                    <strong>{agCount}</strong>
                  </div>
                  <div className="track-info-actions">
                    {streamStatus?.status === 'live' ? (
                      <IconButton
                        disabled={streamStopping}
                        icon={<Square size={15} />}
                        label={`Stop ${channel.name} stream`}
                        onClick={() => {
                          setOpenInfoChannelId(null)
                          void onStopStream(channel)
                        }}
                        title="Stop stream"
                        variant="danger"
                      />
                    ) : (
                      <IconButton
                        disabled={streamStarting}
                        icon={<Radio size={15} />}
                        label={`Start ${channel.name} stream`}
                        onClick={() => {
                          setOpenInfoChannelId(null)
                          void onStartStream(channel)
                        }}
                        title="Start stream"
                      />
                    )}
                    <IconButton
                      icon={<FlaskConical size={15} />}
                      label={`Open playground for ${channel.name}`}
                      onClick={() => {
                        setOpenInfoChannelId(null)
                        onOpenPlayground(channel)
                      }}
                      title="Open playground"
                    />
                    <IconButton
                      icon={<Edit3 size={15} />}
                      label={`Edit ${channel.name}`}
                      onClick={() => {
                        setOpenInfoChannelId(null)
                        onEdit(channel)
                      }}
                      title="Edit channel"
                    />
                    <IconButton
                      icon={<Trash2 size={15} />}
                      label={`Delete ${channel.name}`}
                      onClick={() => {
                        setOpenInfoChannelId(null)
                        void onDelete(channel)
                      }}
                      title="Delete channel"
                      variant="danger"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        />
      )
    })}
  </MobileEntityList>
)
